import type {
  AccountMFAStatus,
  AdminAccountProfile,
  AdminChangeEmailResult,
  AdminCreateArtistInput,
  AdminCreateArtistResult,
  AdminProfileInput,
  PermissionItem,
  RoleAuditPage,
  RoleHoldersPage,
  RoleRequest,
  RoleResponse,
} from '@/types/access';
import { httpClient } from './httpClient';

const BASE = '/api/auth';

export type MyPermissions = {
  roleId: string;
  permissions: string[];
  scopes: string[];
  isSystemAdmin: boolean;
};

export async function getMyPermissions(): Promise<MyPermissions> {
  const response = await httpClient.get<MyPermissions>(`${BASE}/me/permissions`);

  return response.data;
}

export async function listRoles(includeInactive = false): Promise<RoleResponse[]> {
  const response = await httpClient.get<{ roles: RoleResponse[] }>(`${BASE}/admin/roles`, {
    params: includeInactive ? { includeInactive: true } : undefined,
  });

  return response.data.roles;
}

export async function getRole(id: string): Promise<RoleResponse> {
  const response = await httpClient.get<RoleResponse>(
    `${BASE}/admin/roles/${encodeURIComponent(id)}`,
  );

  return response.data;
}

export async function createRole(data: RoleRequest): Promise<RoleResponse> {
  const response = await httpClient.post<RoleResponse>(`${BASE}/admin/roles`, data);

  return response.data;
}

export async function updateRole(id: string, data: RoleRequest): Promise<RoleResponse> {
  const response = await httpClient.put<RoleResponse>(
    `${BASE}/admin/roles/${encodeURIComponent(id)}`,
    data,
  );

  return response.data;
}

export async function deleteRole(id: string): Promise<void> {
  await httpClient.delete(`${BASE}/admin/roles/${encodeURIComponent(id)}`);
}

export async function getPermissionCatalogue(): Promise<PermissionItem[]> {
  const response = await httpClient.get<{ permissions: PermissionItem[] }>(`${BASE}/permissions`);

  return response.data.permissions;
}

export async function listRoleAccounts(
  roleId: string,
  page = 1,
  limit = 20,
): Promise<RoleHoldersPage> {
  const response = await httpClient.get<RoleHoldersPage>(
    `${BASE}/admin/roles/${encodeURIComponent(roleId)}/accounts`,
    { params: { page, limit } },
  );

  return response.data;
}

export async function setAccountRole(accountId: string, roleId: string): Promise<void> {
  await httpClient.put(`${BASE}/admin/accounts/${encodeURIComponent(accountId)}/role`, { roleId });
}

export async function listRoleAudit(page = 1, limit = 20): Promise<RoleAuditPage> {
  const response = await httpClient.get<RoleAuditPage>(`${BASE}/admin/role-audit`, {
    params: { page, limit },
  });

  return response.data;
}

export async function getAccountMFAStatus(id: string): Promise<AccountMFAStatus> {
  const response = await httpClient.get<AccountMFAStatus>(
    `${BASE}/admin/accounts/${encodeURIComponent(id)}/mfa`,
  );

  return response.data;
}

export async function resetAccountMFA(id: string): Promise<void> {
  await httpClient.post(`${BASE}/admin/accounts/${encodeURIComponent(id)}/reset-mfa`);
}

// createArtistAccount adds a verified account for an artist. The API e-mails a set-password link.
export async function createArtistAccount(
  data: AdminCreateArtistInput,
): Promise<AdminCreateArtistResult> {
  const response = await httpClient.post<AdminCreateArtistResult>(`${BASE}/admin/accounts`, data);

  return response.data;
}

export async function getAccountProfile(id: string): Promise<AdminAccountProfile> {
  const response = await httpClient.get<AdminAccountProfile>(
    `${BASE}/admin/accounts/${encodeURIComponent(id)}/profile`,
  );

  return response.data;
}

export async function updateAccountProfile(
  id: string,
  data: AdminProfileInput,
): Promise<AdminAccountProfile> {
  const response = await httpClient.put<AdminAccountProfile>(
    `${BASE}/admin/accounts/${encodeURIComponent(id)}/profile`,
    data,
  );

  return response.data;
}

// changeAccountEmail moves the account to a new e-mail and signs it out everywhere.
export async function changeAccountEmail(
  id: string,
  email: string,
): Promise<AdminChangeEmailResult> {
  const response = await httpClient.put<AdminChangeEmailResult>(
    `${BASE}/admin/accounts/${encodeURIComponent(id)}/email`,
    { email },
  );

  return response.data;
}
